import React, { useEffect, useMemo, useRef, useState } from "react";
import clsx from "clsx";
// Material
import Typography from "@material-ui/core/Typography";
// Custom Hooks
import useDynamicHotelData from "../../../CustomHooks/useDynamicHotelData";
// Styles
import useStyles from "./SelectOrderType.style.js";
import {
  Button,
  Divider,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
} from "@material-ui/core";
import DeliveryTime from "../DeliveryTime/DeliveryTime.js";
import DateSlotPicker from "../../Checkout/DateSlotPicker/index.js";
import { useDispatch, useSelector } from "react-redux";
import { ORDER_TYPES } from "../../../utils/Constants.js";
import { generateDateSlots } from "../../../utils/utils.js";
import TimePicker from "../../Checkout/TimePicker/index.js";
import moment from "moment";
import { Info } from "@material-ui/icons";
import { clearTipAmount } from "../../../store/Cart/CartActions.js";

const SelectOrderType = ({
  value = null,
  onSelect = () => {},
  formValues,
  handleClose,
  onDateAndTimeSelect,
}) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { hotel } = useSelector((state) => state.hotel);
  const [selectedOderType, setOrderType] = useState(value);
  const [dateSlots, setDateSlots] = useState([]);
  const [dateSlotsError, setDateSlotsError] = useState(false);
  const [date, setDate] = useState(null);
  const [typeOfOrder, setTypeOfOrder] = useState("SCHEDULED");
  const [time, setTime] = useState(null);
  const { theme } = useSelector((state) => state?.theme);
  const [orderTimeType, setOrderTimeType] = useState(null);
  // Bumping this remounts DateSlotPicker so its uncontrolled internal
  // selectedDate resets and its slot-sync effect picks `slots[0]` (the
  // first available date). Used to satisfy "when switching back to
  // SCHEDULED from ASAP, the date should be the first available, not
  // the date previously left over from an earlier SCHEDULED visit."
  const [dateSlotResetKey, setDateSlotResetKey] = useState(0);
  const hasMounted = useRef(false);
  const {
    getOrderTypes,
    getDeliveryIntervalSlots,
    getTodayDeliveryIntervalSlots,
    isAsapAvailable,
    isMultipleOrderType,
    getAsapTime,
    availableUntilLabel,
    isMidnightStore,
  } = useDynamicHotelData();
  const [newChange, setNewChange] = useState(false);
  // state which return is shedulued time have value or not
  const isScheduledAvailable = useMemo(() => {
    return (
      (Array.isArray(getTodayDeliveryIntervalSlots) &&
        getTodayDeliveryIntervalSlots.length > 0) ||
      (Array.isArray(getDeliveryIntervalSlots) &&
        getDeliveryIntervalSlots.length > 0)
    );
  }, [getTodayDeliveryIntervalSlots, getDeliveryIntervalSlots]);

  // function for ordertype selection
  const onOrderTypeSelect = (fieldName, type) => {
    setOrderType(type);
    onSelect(fieldName, type);
    dispatch(clearTipAmount());
  };

  // Select first order type if their is multiple ordertypes
  useEffect(() => {
    if (!formValues?.orderType && getOrderTypes?.length > 0) {
      onOrderTypeSelect("orderType", getOrderTypes[0].type);
    }
  }, [formValues?.orderType, getOrderTypes]);

  // switch to next orderType if, default selected ordertype has no asap and sheduled time
  useEffect(() => {
    if (formValues?.orderType && getOrderTypes?.length > 0) {
      const timeoutId = setTimeout(() => {
        // Find the current order type in the getOrderTypes array
        const currentOrderType = formValues?.orderType;

        // Find the type to switch to, based on the current orderType
        const nextOrderType = getOrderTypes.find(
          (typeObj) => typeObj.type !== currentOrderType
        );

        // If there's a valid type to switch to, update the orderType
        if (!isAsapAvailable && !isScheduledAvailable && nextOrderType) {
          onOrderTypeSelect("orderType", nextOrderType.type);
        }
      }, 1000); // 2-second timeout

      // Cleanup function to clear the timeout if dependencies change
      return () => clearTimeout(timeoutId);
    }
  }, [
    formValues?.orderType,
    getOrderTypes,
    isAsapAvailable,
    isScheduledAvailable,
  ]);

  // Select ordertimetype by default
  useEffect(() => {

    if (!newChange) {
      // Match the disable check in the ASAP radio: ASAP can only be the default
      // when the slot list actually contains an ASAP entry (getLabel != null).
      const slotsForAsapCheck =
        getTodayDeliveryIntervalSlots.length > 0
          ? getTodayDeliveryIntervalSlots
          : getDeliveryIntervalSlots;
      const asapSlotAvailable = !!getLabel(slotsForAsapCheck);

      // Don't gate the ASAP default on `hotel.openingAvailable`: the
      // backend buffer-adjusts that flag and flips it false in the
      // final-buffer-minutes window before close, contradicting the
      // "ASAP available until close" rule. `isAsapAvailable` and
      // `asapSlotAvailable` (which are now both gated only on the
      // running-hours window + channel enabled) are the source of truth.
      const canDefaultToAsap =
        isAsapAvailable &&
        asapSlotAvailable &&
        (formValues?.orderType === "DELIVERY" ||
          formValues?.orderType === "PICKUP");

      if (!hasMounted.current && formValues?.orderType) {
        const initialOrderTimeType = canDefaultToAsap ? "ASAP" : "SCHEDULED";

        setOrderTimeType(initialOrderTimeType);
        handleOrderTime(initialOrderTimeType);

        hasMounted.current = true;
      } else {
        handleOrderTime(canDefaultToAsap ? "ASAP" : "SCHEDULED");
        setOrderTimeType(canDefaultToAsap ? "ASAP" : "SCHEDULED");
      }
    }
  }, [
    isAsapAvailable,
    formValues,
    hotel?.openingAvailable,
    newChange,
    getTodayDeliveryIntervalSlots,
    getDeliveryIntervalSlots,
  ]);

  const generateDatePickerSlots = () => {
    let orderTypeValue = value;
    if (value === ORDER_TYPES.TABLE_BOOKING_AND_ORDERING) {
      orderTypeValue = ORDER_TYPES.OPENING;
    }
    const dates = generateDateSlots(hotel, orderTypeValue) || [];
    setDateSlots(dates);
    if (!dates || !dates?.length) {
      setDateSlotsError(true);
    }
  };

  useEffect(() => {
    if (!date) return;
    const isFutureDate = !moment(date).isSame(moment(), "date");
    let dTime;
    if (typeOfOrder === "ASAP") {
      // ASAP is "right now from today" regardless of which date is
      // currently selected in the date picker. When today's SCHEDULED
      // list is empty (shop closes within `buffer` minutes), the date
      // picker auto-jumps to tomorrow — but if the user then picks the
      // ASAP radio, the slot's value is today's `now + buffer` clock
      // string. Combining that string with tomorrow's date would push
      // the cart's deliveryTime to tomorrow with the ASAP time, which
      // then drives the checkout date picker to tomorrow and breaks
      // place-order validation. Anchor ASAP dTime to today instead.
      const todayStr = moment().format("LL");
      dTime = moment(
        `${todayStr}${time ? ` ${time}` : ""}`
      ).toISOString();
    } else {
      // For a future date with no time picked yet (e.g. when
      // DateSlotPicker auto-jumped past Today because today's slots are
      // empty), push the cart's deliveryTime forward to that date's
      // start-of-day so getDeliveryIntervalSlots recomputes for the new
      // day-of-week. The auto-select effect below then populates the
      // first scheduled slot.
      dTime =
        isFutureDate && !time
          ? moment(date).startOf("day").toISOString()
          : moment(`${date}${time ? ` ${time}` : ""}`).toISOString();
    }
    onDateAndTimeSelect(typeOfOrder || "SCHEDULED", dTime);
  }, [date, time, typeOfOrder]);

  useEffect(() => {
    if (hotel || formValues?.orderType || value) {
      generateDatePickerSlots();
    }
  }, [hotel, formValues?.orderType, value]);

  // Auto-select first scheduled time slot when future date slots become available
  const hasAutoSelectedFutureSlot = useRef(false);
  useEffect(() => {
    const isFutureDate = date && !moment(date).isSame(moment(), "date");
    // The slot list (`getDeliveryIntervalSlots`) is keyed off
    // `formValues.deliveryTime`. When the user picks a new date, this
    // effect fires once with the OLD slot list (still pointing at the
    // previous date) before the dispatched deliveryTime update
    // propagates. If we set the time + lock the ref at that point, the
    // re-fire after slots actually recompute for the new date is
    // skipped — so a tomorrow with a different first slot (e.g. a
    // morning-extension block giving "12:15 am") keeps showing the
    // previous selection. Gate on the slot list matching the selected
    // date so we only commit when slots actually reflect it.
    const slotsAreForSelectedDate =
      !!formValues?.deliveryTime &&
      !!date &&
      moment(formValues.deliveryTime).isSame(moment(date), "date");
    if (
      orderTimeType === "SCHEDULED" &&
      date &&
      isFutureDate &&
      getDeliveryIntervalSlots?.length > 0 &&
      !hasAutoSelectedFutureSlot.current &&
      slotsAreForSelectedDate
    ) {
      const scheduledSlots = getDeliveryIntervalSlots.filter(
        (s) => s.typeOfOrder === "SCHEDULED"
      );
      if (scheduledSlots.length > 0) {
        hasAutoSelectedFutureSlot.current = true;
        setTime(scheduledSlots[0].value);
        setTypeOfOrder("SCHEDULED");
      }
    }
  }, [
    getDeliveryIntervalSlots,
    date,
    orderTimeType,
    formValues?.deliveryTime,
  ]);

  // Hide "Today" from the date picker when today has no usable SCHEDULED
  // slots (hours ended, closures wiped them out, or the only remaining
  // grid time is within the prep buffer of close so no schedulable
  // option exists). An ASAP entry alone is not a scheduling option —
  // the date picker is about *scheduling for a date*, and ASAP is "right
  // now" which is implicit. Letting DateSlotPicker auto-select the first
  // remaining entry naturally drops the user on tomorrow in this case,
  // and the existing orderTimeType + first-slot auto-select effects
  // populate the first time.
  const visibleDateSlots = useMemo(() => {
    if (!dateSlots || dateSlots.length === 0) return dateSlots || [];
    const hasTodayScheduled = getTodayDeliveryIntervalSlots.some(
      (s) => s.typeOfOrder === "SCHEDULED"
    );
    if (hasTodayScheduled) return dateSlots;
    return dateSlots.filter((d) => d.label !== "Today");
  }, [dateSlots, getTodayDeliveryIntervalSlots]);

  const onClick = () => {
    setNewChange(true);
  };

  const onSelectDataAndTime = ({ value, typeOfOrder } = {}, type) => {
    if (!value) return;
    if (typeOfOrder) {
      setTypeOfOrder(typeOfOrder);
    }
    if (type === "date") {
      // Set the selected date
      setDate(value);
      hasAutoSelectedFutureSlot.current = false;

      // ASAP is semantically invalid for a future date ("as soon as possible"
      // refers to right now). Without this, when the user jumps from Today
      // (orderTimeType=ASAP) to Tomorrow, the auto-select effect below stays
      // gated off and `time` keeps today's ASAP value — which then matches
      // against tomorrow's evening slot in TimePicker, looking like the
      // past-filter was applied to tomorrow.
      const isFuture = !moment(value).isSame(moment(), "date");
      if (isFuture) {
        if (orderTimeType !== "SCHEDULED") {
          setOrderTimeType("SCHEDULED");
        }
        setTypeOfOrder("SCHEDULED");
      }

      // Automatically select the first available time slot for the new date
      const availableTimeSlots = getAvailableTimeSlotsForDate(value);
      if (availableTimeSlots && availableTimeSlots.length > 0) {
        const firstTimeSlot = availableTimeSlots[0].value; // Assuming time slot object has a 'value' property
        setTime(firstTimeSlot); // Set the first available time
      }
    }

    if (type === "time") {
      setTime(value); // Set time manually if time is selected
    }
  };

  // Function to get the available time slots for a given date
  const getAvailableTimeSlotsForDate = (selectedDate) => {
    // Get the time slots for today or another date based on the selected date
    const availableSlots = moment(selectedDate).isSame(moment(), "date")
      ? getTodayDeliveryIntervalSlots
      : getDeliveryIntervalSlots;

    // Add condition to filter for "SCHEDULED" time slots if orderTimeType is "SCHEDULED"
    if (orderTimeType === "SCHEDULED") {
      return availableSlots.filter((slot) => slot.typeOfOrder === "SCHEDULED");
    }

    return availableSlots; // Return all available slots if not filtered
  };

  const getLabel = (arry) => {
    const asapSlot = arry.find((item) => item.typeOfOrder === "ASAP");
    return asapSlot ? asapSlot.label : undefined;
  };

  const filteredOrderTime = (array) => {
    const result = array.find((arr) => arr?.typeOfOrder === "ASAP");

    return result;
  };

  const handleOrderTime = (e) => {
    setOrderTimeType(e);
    if (e === "ASAP") {
      const filteredTime = filteredOrderTime(
        getTodayDeliveryIntervalSlots?.length > 0
          ? getTodayDeliveryIntervalSlots
          : getDeliveryIntervalSlots
      );
      // ASAP means "right now from today". Force the local date state
      // back to today and lock the future-slot auto-select ref so the
      // downstream chain doesn't bounce back into a SCHEDULED override:
      //   - Without resetting `date`, the dispatch useEffect dispatches
      //     today's ASAP time but the picker's `date` stays on a
      //     previously-selected future day. Other effects keyed on
      //     `isFutureDate` then re-fire and can flip the radio back to
      //     SCHEDULED.
      //   - Without locking `hasAutoSelectedFutureSlot`, if `date`
      //     hasn't propagated yet, the auto-select-future-slot effect
      //     can fire with a stale state and overwrite the ASAP time.
      const todayStr = moment().format("LL");
      if (date !== todayStr) {
        setDate(todayStr);
      }
      hasAutoSelectedFutureSlot.current = true;
      onSelectDataAndTime(filteredTime, "ASAP");
      onSelectDataAndTime(filteredTime, "time");
    } else if (e === "SCHEDULED") {
      // When the user explicitly switches BACK to SCHEDULED from ASAP,
      // remount DateSlotPicker so its internal selectedDate resets to
      // the first available date instead of carrying over a stale
      // selection (e.g. tomorrow from an earlier SCHEDULED visit).
      // Gated on the actual transition so SCHEDULED→SCHEDULED no-ops
      // and the initial-mount handleOrderTime call (orderTimeType
      // starts null) doesn't remount unnecessarily.
      if (orderTimeType === "ASAP") {
        setDateSlotResetKey((k) => k + 1);
      }
      // Respect the date currently selected in the picker. This effect
      // fires whenever `formValues` changes — including the post-
      // dispatch propagation after the user navigates to a future date.
      // Without the `isCurrentDateFuture` guard below, the function
      // would fall back into today's-slots branch and overwrite the
      // auto-select-future-slot effect's correct setTime for
      // tomorrow's first slot, leaving the dropdown frozen on today's
      // first slot even though the date picker shows tomorrow.
      const isCurrentDateFuture =
        date && !moment(date).isSame(moment(), "date");
      const isTodayInDateSlots = visibleDateSlots?.some(
        (slot) => slot.label === "Today"
      );
      const todayScheduled =
        !isCurrentDateFuture && isTodayInDateSlots
          ? getTodayDeliveryIntervalSlots?.filter(
              (s) => s.typeOfOrder === "SCHEDULED"
            )
          : [];
      const futureScheduled = getDeliveryIntervalSlots?.filter(
        (s) => s.typeOfOrder === "SCHEDULED"
      );

      if (todayScheduled?.length > 0) {
        const filteredTime = todayScheduled[0];
        onSelectDataAndTime(filteredTime, "SCHEDULED");
        onSelectDataAndTime(filteredTime, "time");
      } else if (futureScheduled?.length > 0) {
        const filteredTime = futureScheduled[0];
        onSelectDataAndTime(filteredTime, "SCHEDULED");
        onSelectDataAndTime(filteredTime, "time");
      } else if (date && !moment(date).isSame(moment(), "date")) {
        // Both today's and the currently-loaded future-date SCHEDULED
        // lists are empty. This happens when the user was on ASAP, so
        // cart.deliveryTime points at today, and getDeliveryIntervalSlots
        // is therefore still today's (which contains only the ASAP
        // entry). DateSlotPicker has already moved to tomorrow in the
        // UI, but no slot recompute has been triggered for it yet.
        // Kick deliveryTime forward to the displayed future date so
        // getDeliveryIntervalSlots recomputes — the auto-select-future-
        // slot effect then picks tomorrow's first SCHEDULED entry on
        // the next render. Reset hasAutoSelectedFutureSlot so that
        // effect will fire again (it was set true earlier when the
        // initial future-date auto-select ran).
        hasAutoSelectedFutureSlot.current = false;
        setTypeOfOrder("SCHEDULED");
        const dTime = moment(date).startOf("day").toISOString();
        onDateAndTimeSelect("SCHEDULED", dTime);
      }
    }
  };

  const getDeliveryType = useMemo(() => {
    return selectedOderType;
  }, [selectedOderType]);

  const getLastTime = (array) => {
    if (!array || array.length === 0) {
      return undefined;
    }

    const lastElement = array[array.length - 1];

    return lastElement?.label;
  };

  return (
    <>
      {/* <div className={classes.headerDiv}>
        {theme?.header_logo ? (
          <div className={classes.headerLogoDiv}>
            <img
              src={`data:image/png;base64, ${theme?.header_logo}`}
              alt="Header Logo"
              className={classes.headerLogo}
            />
          </div>
        ) : (
          <Typography
            className={clsx(classes.title)}
            variant="h1"
            display="block"
          >
            {hotel ? hotel.hotelName : ""}
          </Typography>
        )}
      </div> */}
      {/* {isMultipleOrderType && ( */}
      <div className={classes.modalTitleSection}>
        <Typography
          className={classes.modalTitle}
          variant="h1"
          align="center"
          paragraph
        >
          {"Select Order Type"}
        </Typography>
        <Divider />
      </div>
      {/* )} */}
      {/* {isMultipleOrderType && ( */}
      <div id="checkout-orderTypeForm" className={clsx(classes.container)}>
        {getOrderTypes?.map((o, i) => (
          <div
            key={i}
            className={clsx(
              classes.btnNormal,
              o.type === selectedOderType && classes.item
              // o.active === false && classes.itemDisabled
            )}
            onClick={() => {
              onOrderTypeSelect("orderType", o.type);
              setNewChange(false);
            }}
          >
            <div className={classes.itemDiv}>
              <o.Icon
                className={clsx(
                  o.type === selectedOderType
                    ? classes.itemIcon
                    : classes.normalIcon
                )}
              />
            </div>
            <Typography
              // color="primary"
              className={clsx(
                o.type === selectedOderType
                  ? classes.itemText
                  : classes.normalText
              )}
              variant="caption"
            >
              {o.title}
            </Typography>
          </div>
        ))}
      </div>
      {/* )} */}
      <div className={classes.modalTitleSection} style={{ marginTop: "16px" }}>
        <Typography
          className={classes.modalTitle}
          variant="h1"
          align="center"
          paragraph
        >
          {"Order Time"}
        </Typography>
        <Divider />
      </div>
      <div className={classes.timeSelectDiv}>
        {isMidnightStore && (
          <div className={classes.lateNightTag}>
            Late night orders available
          </div>
        )}
        <FormControl>
          <RadioGroup
            aria-labelledby="demo-radio-buttons-group-label"
            value={orderTimeType}
            name="radio-buttons-group"
            onChange={(e) => handleOrderTime(e.target.value)}
            onClick={onClick}
          >
            <FormControlLabel
              value="ASAP"
              className={classes.radioLabel}
              control={
                <Radio
                  className={classes.radioBtn}
                  classes={{ checked: classes.checked }}
                />
              }
              label={
                !isAsapAvailable ||
                !getLabel(
                  getTodayDeliveryIntervalSlots.length > 0
                    ? getTodayDeliveryIntervalSlots
                    : getDeliveryIntervalSlots
                )
                  ? "ASAP : Unavailable"
                  : `ASAP : ${getDeliveryType} in ${getLabel(
                      getTodayDeliveryIntervalSlots.length > 0
                        ? getTodayDeliveryIntervalSlots
                        : getDeliveryIntervalSlots
                    )}`
              }
              checked={orderTimeType === "ASAP"}
              disabled={
                !isAsapAvailable ||
                !getLabel(
                  getTodayDeliveryIntervalSlots.length > 0
                    ? getTodayDeliveryIntervalSlots
                    : getDeliveryIntervalSlots
                )
              }
            />
            <FormControlLabel
              value="SCHEDULED"
              className={classes.radioLabel}
              control={
                <Radio
                  className={classes.radioBtn}
                  classes={{ checked: classes.checked }}
                />
              }
              label="Schedule for later"
              checked={orderTimeType === "SCHEDULED"}
              // onChange={(e) => handleOrderTime(e.target.value)}
            />
          </RadioGroup>
        </FormControl>
        <div
          id="checkout-orderTypeForm"
          className={clsx(
            orderTimeType === "SCHEDULED"
              ? classes.sheduledContainer
              : classes.hideContainer
          )}
        >
          <DateSlotPicker
            key={dateSlotResetKey}
            slots={visibleDateSlots}
            className={`w-100 ${classes.dateTimeSelection}`}
            onChange={onSelectDataAndTime}
          />
          <TimePicker
            slots={
              moment(formValues.deliveryTime).isSame(moment(), "date") &&
              // filteredTimeSlots(
              getTodayDeliveryIntervalSlots.length > 0
                ? getTodayDeliveryIntervalSlots
                : getDeliveryIntervalSlots
              // )
            }
            shouldFilter={true}
            date={date}
            onClick={onClick}
            orderTimeType={orderTimeType}
            className={`w-100 ${classes.dateTimeSelection}`}
            onChange={onSelectDataAndTime}
          />
        </div>

        {(availableUntilLabel ||
          getLastTime(
            getTodayDeliveryIntervalSlots.length > 0
              ? getTodayDeliveryIntervalSlots
              : getDeliveryIntervalSlots
          )) &&
          formValues?.deliveryType === "ASAP" &&
          getDeliveryType &&
          isAsapAvailable &&
          hotel?.openingAvailable && (
            <div className={classes.infoDiv}>
              <Info className={classes.infoIcon} />
              <Typography className={classes.infoText}>
                <span style={{ fontWeight: "800" }}>{getDeliveryType}</span>{" "}
                available until{" "}
                <span style={{ fontWeight: "800" }}>
                  {availableUntilLabel ||
                    getLastTime(
                      getTodayDeliveryIntervalSlots.length > 0
                        ? getTodayDeliveryIntervalSlots
                        : getDeliveryIntervalSlots
                    )}
                </span>
              </Typography>
            </div>
          )}
      </div>

      {/* {orderTimeType === "shedulued" && ( */}
      {/* )} */}
      <div className={classes.btnDiv}>
        <Button
          disabled={!formValues?.orderType}
          className={classes.continueBtn}
          onClick={handleClose}
        >
          Continue
        </Button>
      </div>
    </>
  );
};

export default SelectOrderType;
